/**
 * Pulp & Paper Industry Configuration
 *
 * Supports: Paper Machines, Digesters, Pulpers, Refiners, Rewinders, Coating Equipment
 */

import { IndustryConfig } from '../industryTypes'

export const pulpPaperConfig: IndustryConfig = {
  id: 'pulp_paper',
  name: 'Pulp & Paper',
  description: 'Pulp manufacturing and paper production operations',
  icon: 'FileText',

  terminology: {
    asset: 'Equipment',
    assets: 'Equipment',
    assetPlural: 'Equipment',
    assetType: 'Equipment Type',
    failure: 'Breakdown',
    health: 'Condition',
    healthScore: 'Health Score',
    healthy: 'Running',
    degraded: 'Needs Attention',
    critical: 'Down',
    alert: 'Alert',
    process: 'Production',
    maintenance: 'Maintenance',
    inspection: 'Inspection',
    downtime: 'Downtime',
    workOrder: 'Work Order',
    prediction: 'Forecast',
    anomaly: 'Anomaly',
    trend: 'Trend',
    risk: 'Risk',
    facility: 'Mill',
    facilityPlural: 'Mills',
    location: 'Section',
    operator: 'Machine Tender',
    supervisor: 'Shift Superintendent',
    calibration: 'Calibration',
    compliance: 'Environmental Compliance',
    criticality: 'Criticality',
  },

  assetTypes: [
    {
      id: 'paper_machine',
      name: 'Paper Machine',
      icon: 'Boxes',
      description: 'Fourdrinier or gap former paper machines',
      category: 'Production',
      defaultMetrics: ['speed', 'basis_weight', 'moisture', 'sheet_breaks'],
      maintenanceTypes: ['felt_change', 'wire_change', 'roll_service', 'dryer_section_maintenance'],
      criticalityLevels: [
        { id: 'critical', name: 'Primary', color: '#ef4444', description: 'Main production machine', downtimeCostMultiplier: 2000 },
        { id: 'high', name: 'Secondary', color: '#f59e0b', description: 'Secondary production', downtimeCostMultiplier: 1000 },
        { id: 'medium', name: 'Specialty', color: '#3b82f6', description: 'Specialty grades', downtimeCostMultiplier: 500 },
        { id: 'low', name: 'Tissue', color: '#6b7280', description: 'Tissue production', downtimeCostMultiplier: 300 },
      ],
    },
    {
      id: 'digester',
      name: 'Digester',
      icon: 'Container',
      description: 'Batch or continuous kraft digesters',
      category: 'Pulping',
      defaultMetrics: ['temperature', 'pressure', 'kappa_number', 'h_factor'],
      maintenanceTypes: ['inspection', 'strainer_cleaning', 'valve_service', 'turnaround'],
      criticalityLevels: [
        { id: 'critical', name: 'Continuous', color: '#ef4444', description: 'Continuous digester', downtimeCostMultiplier: 1500 },
        { id: 'high', name: 'Batch Primary', color: '#f59e0b', description: 'Primary batch digester', downtimeCostMultiplier: 800 },
        { id: 'medium', name: 'Batch Secondary', color: '#3b82f6', description: 'Secondary batch digester', downtimeCostMultiplier: 400 },
        { id: 'low', name: 'Spare', color: '#6b7280', description: 'Spare digester', downtimeCostMultiplier: 100 },
      ],
    },
    {
      id: 'refiner',
      name: 'Refiner',
      icon: 'Disc',
      description: 'Disc refiners for pulp processing',
      category: 'Stock Preparation',
      defaultMetrics: ['specific_energy', 'freeness', 'plate_gap', 'motor_load'],
      maintenanceTypes: ['plate_change', 'bearing_service', 'alignment', 'motor_service'],
      criticalityLevels: [
        { id: 'critical', name: 'Primary', color: '#ef4444', description: 'Primary refining', downtimeCostMultiplier: 600 },
        { id: 'high', name: 'Secondary', color: '#f59e0b', description: 'Secondary refining', downtimeCostMultiplier: 300 },
        { id: 'medium', name: 'Tertiary', color: '#3b82f6', description: 'Tertiary refining', downtimeCostMultiplier: 150 },
        { id: 'low', name: 'Broke', color: '#6b7280', description: 'Broke refining', downtimeCostMultiplier: 50 },
      ],
    },
    {
      id: 'recovery_boiler',
      name: 'Recovery Boiler',
      icon: 'Flame',
      description: 'Black liquor recovery boilers',
      category: 'Recovery',
      defaultMetrics: ['steam_production', 'smelt_flow', 'emissions', 'efficiency'],
      maintenanceTypes: ['sootblowing', 'tube_inspection', 'refractory_repair', 'annual_outage'],
      criticalityLevels: [
        { id: 'critical', name: 'Primary', color: '#ef4444', description: 'Primary recovery boiler', downtimeCostMultiplier: 3000 },
        { id: 'high', name: 'Secondary', color: '#f59e0b', description: 'Secondary recovery boiler', downtimeCostMultiplier: 1500 },
        { id: 'medium', name: 'Power', color: '#3b82f6', description: 'Power boiler', downtimeCostMultiplier: 500 },
        { id: 'low', name: 'Auxiliary', color: '#6b7280', description: 'Auxiliary boiler', downtimeCostMultiplier: 200 },
      ],
    },
    {
      id: 'rewinder',
      name: 'Rewinder',
      icon: 'RotateCw',
      description: 'Paper rewinders and slitters',
      category: 'Finishing',
      defaultMetrics: ['speed', 'tension', 'trim_width', 'roll_quality'],
      maintenanceTypes: ['knife_change', 'bearing_service', 'roll_service', 'brake_adjustment'],
      criticalityLevels: [
        { id: 'critical', name: 'Primary', color: '#ef4444', description: 'Main rewinder', downtimeCostMultiplier: 400 },
        { id: 'high', name: 'Secondary', color: '#f59e0b', description: 'Secondary rewinder', downtimeCostMultiplier: 200 },
        { id: 'medium', name: 'Slitter', color: '#3b82f6', description: 'Slitter rewinder', downtimeCostMultiplier: 100 },
        { id: 'low', name: 'Wrapper', color: '#6b7280', description: 'Wrapper line', downtimeCostMultiplier: 50 },
      ],
    },
  ],

  kpiDomains: [
    {
      id: 'production_efficiency',
      name: 'Production Efficiency',
      icon: 'TrendingUp',
      description: 'Paper machine efficiency metrics',
      businessValue: 'Maximize production output and minimize waste',
      category: 'universal',
      metrics: [
        { id: 'machine_efficiency', name: 'Machine Efficiency', unit: '%', description: 'Overall machine efficiency', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 90, warning: 85, critical: 80 } },
        { id: 'broke_rate', name: 'Broke Rate', unit: '%', description: 'Percentage of broke produced', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 3, warning: 5, critical: 8 } },
        { id: 'production_rate', name: 'Production Rate', unit: 'tonnes/h', description: 'Hourly production rate', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 50, warning: 45, critical: 40 } },
      ],
      thresholds: { healthy: { min: 90 }, warning: { min: 85, max: 90 }, critical: { max: 85 } },
      visualizations: ['gauge', 'line_chart', 'comparison', 'trend'],
    },
    {
      id: 'quality_metrics',
      name: 'Quality Metrics',
      icon: 'CheckSquare',
      description: 'Paper quality measurements',
      businessValue: 'Meet customer specifications and reduce claims',
      category: 'universal',
      metrics: [
        { id: 'basis_weight_cv', name: 'Basis Weight CV', unit: '%', description: 'Coefficient of variation', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 1.0, warning: 1.5, critical: 2.0 } },
        { id: 'moisture_cv', name: 'Moisture CV', unit: '%', description: 'Moisture variation', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 0.5, warning: 1.0, critical: 1.5 } },
        { id: 'first_pass_yield', name: 'First Pass Yield', unit: '%', description: 'On-spec first pass', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 98, warning: 95, critical: 90 } },
      ],
      thresholds: { healthy: { max: 1.0 }, warning: { min: 1.0, max: 1.5 }, critical: { min: 1.5 } },
      visualizations: ['line_chart', 'histogram', 'control_chart', 'trend'],
    },
    {
      id: 'pulping_performance',
      name: 'Pulping Performance',
      icon: 'Layers',
      description: 'Pulp mill performance metrics',
      businessValue: 'Optimize pulp quality and yield',
      category: 'industry_specific',
      metrics: [
        { id: 'kappa_number', name: 'Kappa Number', unit: '', description: 'Pulp delignification level', dataType: 'continuous', aggregation: 'avg', goodDirection: 'target', defaultThresholds: { healthy: 25, warning: 28, critical: 32 } },
        { id: 'pulp_yield', name: 'Pulp Yield', unit: '%', description: 'Wood to pulp yield', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 48, warning: 46, critical: 44 } },
        { id: 'freeness', name: 'Freeness', unit: 'CSF', description: 'Canadian Standard Freeness', dataType: 'continuous', aggregation: 'avg', goodDirection: 'target', defaultThresholds: { healthy: 500, warning: 450, critical: 400 } },
      ],
      thresholds: { healthy: { min: 48 }, warning: { min: 45, max: 48 }, critical: { max: 45 } },
      visualizations: ['line_chart', 'scatter', 'trend', 'comparison'],
    },
    {
      id: 'environmental',
      name: 'Environmental',
      icon: 'Leaf',
      description: 'Environmental performance metrics',
      businessValue: 'Meet EPA and environmental regulations',
      category: 'industry_specific',
      metrics: [
        { id: 'bod_discharge', name: 'BOD Discharge', unit: 'kg/tonne', description: 'Biological oxygen demand', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 3, warning: 5, critical: 8 } },
        { id: 'trs_emissions', name: 'TRS Emissions', unit: 'ppm', description: 'Total reduced sulfur', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 5, warning: 10, critical: 20 } },
        { id: 'water_usage', name: 'Water Usage', unit: 'm3/tonne', description: 'Fresh water consumption', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 20, warning: 30, critical: 40 } },
      ],
      thresholds: { healthy: { max: 3 }, warning: { min: 3, max: 5 }, critical: { min: 5 } },
      visualizations: ['line_chart', 'bar_chart', 'trend', 'gauge'],
    },
  ],

  maintenanceTypes: [
    {
      id: 'felt_change',
      name: 'Felt Change',
      description: 'Press or dryer felt replacement',
      category: 'preventive',
      defaultInterval: { value: 6, unit: 'weeks' },
      laborRequirements: [
        { role: 'Machine Crew', count: 6, hourlyRate: 45 },
        { role: 'Supervisor', count: 1, hourlyRate: 65 },
      ],
      estimatedDuration: { min: 4, typical: 6, max: 10, unit: 'hours' },
      skillsRequired: ['felt_installation', 'machine_operation'],
      partsCommonly: ['press_felt', 'dryer_felt', 'seaming_tools'],
    },
    {
      id: 'roll_service',
      name: 'Roll Service',
      description: 'Roll grinding and cover replacement',
      category: 'condition_based',
      defaultInterval: { value: 12, unit: 'months' },
      laborRequirements: [
        { role: 'Roll Technician', count: 2, hourlyRate: 55 },
        { role: 'Millwright', count: 2, hourlyRate: 50 },
      ],
      estimatedDuration: { min: 8, typical: 16, max: 24, unit: 'hours' },
      skillsRequired: ['roll_handling', 'rigging', 'alignment'],
      partsCommonly: ['roll_covers', 'bearings', 'seals'],
    },
    {
      id: 'digester_inspection',
      name: 'Digester Inspection',
      description: 'Internal inspection and repairs',
      category: 'preventive',
      defaultInterval: { value: 24, unit: 'months' },
      laborRequirements: [
        { role: 'Inspector', count: 2, hourlyRate: 70 },
        { role: 'Welder', count: 2, hourlyRate: 55 },
      ],
      estimatedDuration: { min: 3, typical: 5, max: 7, unit: 'days' },
      skillsRequired: ['confined_space', 'ndt_inspection', 'welding'],
      partsCommonly: ['strainer_plates', 'nozzles', 'valves'],
    },
  ],

  riskFactors: [
    { id: 'breakdown_risk', name: 'Breakdown Risk', description: 'Risk of unplanned stoppage', category: 'operational', weight: 0.35, calculationMethod: 'exponential', inputs: ['health_score', 'age', 'utilization'] },
    { id: 'quality_risk', name: 'Quality Risk', description: 'Risk of off-spec production', category: 'quality', weight: 0.25, calculationMethod: 'linear', inputs: ['cv_values', 'defect_rate'] },
    { id: 'environmental_risk', name: 'Environmental Risk', description: 'Risk of environmental violation', category: 'environmental', weight: 0.25, calculationMethod: 'threshold', inputs: ['emissions', 'discharge'] },
    { id: 'safety_risk', name: 'Safety Risk', description: 'Risk to personnel safety', category: 'safety', weight: 0.15, calculationMethod: 'threshold', inputs: ['condition', 'guarding'] },
  ],

  complianceStandards: [
    {
      id: 'epa_cluster',
      name: 'EPA Cluster Rules',
      shortName: 'Cluster',
      description: 'Air and water emissions standards for pulp and paper',
      authority: 'EPA',
      region: ['US'],
      requirements: [
        { id: 'hap_limits', name: 'HAP Limits', description: 'Hazardous air pollutant limits', kpiId: 'hap_emissions', threshold: 100, operator: 'lte', severity: 'critical' },
        { id: 'bod_limits', name: 'BOD Limits', description: 'Effluent BOD limits', kpiId: 'bod_discharge', threshold: 100, operator: 'lte', severity: 'critical' },
      ],
      reportingFrequency: 'monthly',
    },
    {
      id: 'fsc_coc',
      name: 'FSC Chain of Custody',
      shortName: 'FSC',
      description: 'Forest Stewardship Council certification',
      authority: 'FSC',
      region: ['Global'],
      requirements: [
        { id: 'material_tracking', name: 'Material Tracking', description: 'Track certified material', kpiId: 'fsc_percentage', threshold: 100, operator: 'eq', severity: 'required' },
        { id: 'labeling', name: 'Label Control', description: 'Proper FSC labeling', kpiId: 'label_compliance', threshold: 100, operator: 'eq', severity: 'required' },
      ],
      reportingFrequency: 'annual',
    },
  ],

  visualization: {
    primaryColor: '#84cc16',
    secondaryColor: '#22d3ee',
    healthyColor: '#22c55e',
    warningColor: '#f59e0b',
    criticalColor: '#ef4444',
    chartDefaults: {
      showGrid: true,
      showLegend: true,
      animationDuration: 300,
    },
  },

  narrativeTemplates: [
    {
      id: 'health_summary',
      name: 'Asset Health Summary',
      context: 'asset_health',
      audience: ['manager', 'engineer', 'technician'],
      template: 'Your {assetType} "{assetName}" at {facility} is currently {healthStatus}. {trendDescription} Current efficiency: {efficiency}%. {recommendationText}',
      variables: [
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'facility', type: 'terminology' },
        { name: 'healthStatus', type: 'terminology' },
        { name: 'trendDescription', type: 'metric' },
        { name: 'efficiency', type: 'percentage' },
        { name: 'recommendationText', type: 'metric' },
      ],
    },
  ],

  features: {
    enableBenchmarking: true,
    enableWhatIf: true,
    enableCollaboration: true,
    enableCompliance: true,
    enableKnowledgeCapture: true,
  },

  defaults: {
    timeRange: '24h',
    refreshInterval: 30,
    defaultDashboard: 'mill-operations',
  },

  professionalRoles: [
    {
      id: 'machine_tender',
      category: 'operator' as const,
      name: 'Machine Tender',
      shortName: 'Tender',
      description: 'Operates and monitors paper machine',
      responsibilities: ['Machine operation', 'Grade changes', 'Quality monitoring'],
      focusAreas: ['Sheet quality', 'Machine efficiency', 'Safety'],
      explanationStyle: {
        depth: 'practical' as const,
        includeMetrics: true,
        includeCosts: false,
        includeRootCause: false,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'practical' as const,
        focusOn: ['action_items', 'safety'] as const,
      },
      decisionAuthority: ['Speed adjustments', 'Minor corrections'],
      reportingTo: 'Shift Superintendent',
      icon: 'User',
    },
    {
      id: 'process_engineer',
      category: 'sme' as const,
      name: 'Process Engineer',
      shortName: 'Proc Eng',
      description: 'Optimizes pulp and paper processes',
      responsibilities: ['Process optimization', 'Quality improvement', 'Troubleshooting'],
      focusAreas: ['Pulp quality', 'Paper properties', 'Energy efficiency'],
      explanationStyle: {
        depth: 'technical' as const,
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical' as const,
        focusOn: ['technical_details', 'root_cause', 'optimization'] as const,
      },
      decisionAuthority: ['Process changes', 'Chemical adjustments'],
      reportingTo: 'Technical Manager',
      icon: 'Settings',
    },
    {
      id: 'mill_operations_manager',
      category: 'executive' as const,
      name: 'Mill Operations Manager',
      shortName: 'Ops Mgr',
      description: 'Oversees day-to-day mill operations including production scheduling, staffing, and cost control',
      responsibilities: [
        'Production planning and scheduling',
        'Shift crew management and staffing',
        'Operational cost control and budgeting',
        'Safety and environmental compliance oversight',
        'Coordination between pulp mill and paper machine departments',
      ],
      focusAreas: ['production_rate', 'machine_efficiency', 'downtime', 'cost_per_tonne', 'safety_incidents'],
      explanationStyle: {
        depth: 'detailed' as const,
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'professional' as const,
        focusOn: ['business_impact', 'action_items', 'risk'] as const,
      },
      decisionAuthority: ['Production scheduling', 'Staffing levels', 'Operational expenditure', 'Shutdown approvals'],
      reportingTo: 'Mill Manager',
      icon: 'Building',
    },
    {
      id: 'millwright_technician',
      category: 'technician' as const,
      name: 'Millwright / Maintenance Technician',
      shortName: 'Millwright',
      description: 'Performs hands-on mechanical maintenance, rigging, alignment, and equipment rebuilds in the mill',
      responsibilities: [
        'Equipment installation, alignment, and rigging',
        'Roll changes, felt changes, and wire changes',
        'Pump and valve rebuilds',
        'Precision alignment of rotating equipment',
        'Confined space entries for digester and vessel inspections',
      ],
      focusAreas: ['repair_procedures', 'parts_needed', 'safety_requirements', 'tool_requirements', 'alignment_specs'],
      explanationStyle: {
        depth: 'technical' as const,
        includeMetrics: false,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical' as const,
        focusOn: ['technical_details', 'action_items', 'risk'] as const,
      },
      decisionAuthority: ['Repair methods', 'Parts selection', 'Immediate safety stops', 'Rigging plans'],
      reportingTo: 'Maintenance Supervisor',
      icon: 'Wrench',
    },
    {
      id: 'pulp_quality_analyst',
      category: 'analyst' as const,
      name: 'Pulp Quality Analyst',
      shortName: 'QA',
      description: 'Monitors and analyzes pulp and paper quality through laboratory testing, statistical analysis, and process correlation',
      responsibilities: [
        'Laboratory testing of pulp and paper properties',
        'Statistical process control and trend analysis',
        'Grade change quality verification',
        'Customer complaint investigation',
        'Quality data reporting and dashboard maintenance',
      ],
      focusAreas: ['kappa_number', 'basis_weight_cv', 'moisture_cv', 'brightness', 'tensile_strength', 'freeness'],
      explanationStyle: {
        depth: 'detailed' as const,
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'professional' as const,
        focusOn: ['technical_details', 'business_impact'] as const,
      },
      decisionAuthority: ['Quality hold decisions', 'Test method selection', 'Data collection priorities', 'SPC limit adjustments'],
      reportingTo: 'Quality Manager',
      icon: 'BarChart',
    },
    {
      id: 'environmental_compliance_officer',
      category: 'analyst' as const,
      name: 'Environmental Compliance Officer',
      shortName: 'ECO',
      description: 'Ensures mill operations comply with EPA Cluster Rules, air and water permits, and environmental regulations',
      responsibilities: [
        'Air emissions monitoring and reporting (TRS, HAPs, particulate)',
        'Effluent discharge monitoring (BOD, TSS, color, pH)',
        'Environmental permit management and renewal',
        'Regulatory agency liaison and audit coordination',
        'Spill prevention and emergency response planning',
      ],
      focusAreas: ['trs_emissions', 'bod_discharge', 'water_usage', 'hap_emissions', 'permit_compliance'],
      explanationStyle: {
        depth: 'detailed' as const,
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'professional' as const,
        focusOn: ['compliance', 'risk', 'action_items'] as const,
      },
      decisionAuthority: ['Compliance hold decisions', 'Permit deviation responses', 'Emergency spill actions', 'Regulatory reporting'],
      reportingTo: 'Mill Manager',
      icon: 'Leaf',
    },
  ],

  partsCatalog: {
    categories: [
      {
        id: 'felts_fabrics',
        name: 'Felts & Fabrics',
        description: 'Paper machine clothing including press felts, dryer felts, and forming fabrics',
        parts: [
          {
            id: 'FELT-PRESS-001',
            partNumber: 'FELT-PRESS-001',
            name: 'Press Felt',
            description: 'High-dewatering press felt for wet press section',
            category: 'felts_fabrics',
            manufacturer: 'Albany International',
            unitCost: 35000,
            currency: 'USD',
            leadTime: { value: 8, unit: 'weeks' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['paper_machine'],
            failureModes: ['wear', 'contamination', 'compaction'],
            averageLifespan: { value: 42, unit: 'days' },
            specifications: { width: 'Machine-specific', type: 'Multiaxial batt-on-base' },
          },
          {
            id: 'FELT-DRY-001',
            partNumber: 'FELT-DRY-001',
            name: 'Dryer Felt',
            description: 'Dryer section felt for sheet support and moisture removal',
            category: 'felts_fabrics',
            manufacturer: 'Voith',
            unitCost: 28000,
            currency: 'USD',
            leadTime: { value: 8, unit: 'weeks' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['paper_machine'],
            failureModes: ['wear', 'edge_damage', 'contamination'],
            averageLifespan: { value: 84, unit: 'days' },
            specifications: { width: 'Machine-specific', type: 'Spiral link or woven' },
          },
          {
            id: 'FABRIC-FORM-001',
            partNumber: 'FABRIC-FORM-001',
            name: 'Forming Fabric',
            description: 'Triple-layer forming fabric for Fourdrinier wire section',
            category: 'felts_fabrics',
            manufacturer: 'Albany International',
            unitCost: 45000,
            currency: 'USD',
            leadTime: { value: 10, unit: 'weeks' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['paper_machine'],
            failureModes: ['wear', 'hole_development', 'edge_curl'],
            averageLifespan: { value: 56, unit: 'days' },
            specifications: { mesh: '84x76', type: 'Triple-layer SSB' },
          },
        ],
      },
      {
        id: 'doctor_blades',
        name: 'Doctor Blades & Holders',
        description: 'Doctor blades for cleaning and creping on paper machine rolls',
        parts: [
          {
            id: 'BLADE-CREP-001',
            partNumber: 'BLADE-CREP-001',
            name: 'Creping Doctor Blade',
            description: 'Steel creping blade for Yankee dryer',
            category: 'doctor_blades',
            manufacturer: 'Kadant',
            unitCost: 850,
            currency: 'USD',
            leadTime: { value: 2, unit: 'weeks' as const },
            criticalityLevel: 'essential' as const,
            compatibleAssetTypes: ['paper_machine'],
            failureModes: ['edge_wear', 'chatter', 'blade_breakage'],
            averageLifespan: { value: 8, unit: 'hours' },
            specifications: { material: 'Carbon steel', thickness: '1.0mm', bevel: '25 degrees' },
          },
          {
            id: 'BLADE-CLEAN-001',
            partNumber: 'BLADE-CLEAN-001',
            name: 'Cleaning Doctor Blade',
            description: 'UHMW polyethylene cleaning blade for press and dryer rolls',
            category: 'doctor_blades',
            manufacturer: 'Kadant',
            unitCost: 320,
            currency: 'USD',
            leadTime: { value: 1, unit: 'weeks' as const },
            criticalityLevel: 'essential' as const,
            compatibleAssetTypes: ['paper_machine'],
            failureModes: ['wear', 'scoring', 'buildup'],
            averageLifespan: { value: 14, unit: 'days' },
            specifications: { material: 'UHMW-PE', thickness: '6mm' },
          },
        ],
      },
      {
        id: 'digester_components',
        name: 'Digester Components',
        description: 'Internal components for batch and continuous kraft digesters',
        parts: [
          {
            id: 'SCREEN-DIG-001',
            partNumber: 'SCREEN-DIG-001',
            name: 'Digester Screen Plate',
            description: 'Stainless steel extraction screen plate for continuous digester',
            category: 'digester_components',
            manufacturer: 'AFT (Aikawa)',
            unitCost: 8500,
            currency: 'USD',
            leadTime: { value: 12, unit: 'weeks' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['digester'],
            failureModes: ['plugging', 'corrosion', 'erosion', 'cracking'],
            averageLifespan: { value: 24, unit: 'months' },
            specifications: { material: 'Duplex SS 2205', slotWidth: '3.2mm' },
          },
          {
            id: 'SEAL-CIRC-001',
            partNumber: 'SEAL-CIRC-001',
            name: 'Circulation Pump Mechanical Seal',
            description: 'Double mechanical seal for digester circulation pump handling black liquor',
            category: 'digester_components',
            manufacturer: 'John Crane',
            unitCost: 4200,
            currency: 'USD',
            leadTime: { value: 6, unit: 'weeks' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['digester'],
            failureModes: ['seal_face_wear', 'spring_fatigue', 'chemical_attack'],
            averageLifespan: { value: 18, unit: 'months' },
            specifications: { type: 'Double cartridge', material: 'SiC/SiC faces', size: '4 inch' },
          },
          {
            id: 'VALVE-BL-001',
            partNumber: 'VALVE-BL-001',
            name: 'Blow Valve',
            description: 'High-temperature blow valve for batch digester discharge',
            category: 'digester_components',
            manufacturer: 'Neles (Valmet)',
            unitCost: 15000,
            currency: 'USD',
            leadTime: { value: 16, unit: 'weeks' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['digester'],
            failureModes: ['erosion', 'seat_damage', 'actuator_failure'],
            averageLifespan: { value: 36, unit: 'months' },
            specifications: { size: '12 inch', rating: 'ANSI 300', material: 'Duplex SS' },
          },
        ],
      },
      {
        id: 'recovery_boiler_parts',
        name: 'Recovery Boiler Components',
        description: 'Tubes, sootblower elements, and components for black liquor recovery boilers',
        parts: [
          {
            id: 'TUBE-RB-001',
            partNumber: 'TUBE-RB-001',
            name: 'Recovery Boiler Composite Floor Tube',
            description: 'Co-extruded 304L/SA210 composite tube for smelt bed floor',
            category: 'recovery_boiler_parts',
            manufacturer: 'Sandvik',
            unitCost: 1200,
            currency: 'USD',
            leadTime: { value: 16, unit: 'weeks' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['recovery_boiler'],
            failureModes: ['corrosion', 'smelt_attack', 'cracking', 'thinning'],
            averageLifespan: { value: 10, unit: 'years' },
            specifications: { material: '304L/SA210 composite', od: '63.5mm', wall: '7.0mm' },
          },
          {
            id: 'SOOT-ELEM-001',
            partNumber: 'SOOT-ELEM-001',
            name: 'Sootblower Element',
            description: 'Retractable sootblower lance element with nozzle head for superheater cleaning',
            category: 'recovery_boiler_parts',
            manufacturer: 'Clyde Bergemann',
            unitCost: 6500,
            currency: 'USD',
            leadTime: { value: 8, unit: 'weeks' as const },
            criticalityLevel: 'essential' as const,
            compatibleAssetTypes: ['recovery_boiler'],
            failureModes: ['erosion', 'warping', 'nozzle_plugging', 'tube_bowing'],
            averageLifespan: { value: 24, unit: 'months' },
            specifications: { type: 'Retractable IK-525', travel: '3.6m', pressure: '150 psig steam' },
          },
          {
            id: 'TUBE-SH-001',
            partNumber: 'TUBE-SH-001',
            name: 'Superheater Tube Section',
            description: 'Alloy steel superheater tube for high-temperature steam generation',
            category: 'recovery_boiler_parts',
            manufacturer: 'Vallourec',
            unitCost: 2800,
            currency: 'USD',
            leadTime: { value: 14, unit: 'weeks' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['recovery_boiler'],
            failureModes: ['corrosion', 'erosion', 'creep', 'thermal_fatigue'],
            averageLifespan: { value: 8, unit: 'years' },
            specifications: { material: 'SA-213 T22', od: '50.8mm', wall: '5.6mm' },
          },
        ],
      },
      {
        id: 'lime_kiln_parts',
        name: 'Lime Kiln Components',
        description: 'Refractory, riding rings, and mechanical components for rotary lime kilns',
        parts: [
          {
            id: 'REFR-LK-001',
            partNumber: 'REFR-LK-001',
            name: 'Lime Kiln Refractory Brick',
            description: 'High-alumina refractory brick for hot zone lining',
            category: 'lime_kiln_parts',
            manufacturer: 'RHI Magnesita',
            unitCost: 85,
            currency: 'USD',
            leadTime: { value: 6, unit: 'weeks' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['recovery_boiler'],
            failureModes: ['spalling', 'chemical_attack', 'thermal_shock', 'erosion'],
            averageLifespan: { value: 5, unit: 'years' },
            specifications: { material: '70% Al2O3', dimensions: '230x114x76mm', serviceTemp: '1400C' },
          },
          {
            id: 'RING-RIDE-001',
            partNumber: 'RING-RIDE-001',
            name: 'Riding Ring (Tire)',
            description: 'Forged steel riding ring for rotary lime kiln support',
            category: 'lime_kiln_parts',
            manufacturer: 'FLSmidth',
            unitCost: 85000,
            currency: 'USD',
            leadTime: { value: 26, unit: 'weeks' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['recovery_boiler'],
            failureModes: ['wear', 'cracking', 'misalignment', 'ovality'],
            averageLifespan: { value: 20, unit: 'years' },
            specifications: { material: 'Forged 4140 steel', od: 'Kiln-specific' },
          },
          {
            id: 'SHELL-LK-001',
            partNumber: 'SHELL-LK-001',
            name: 'Kiln Shell Section',
            description: 'Replacement shell plate section for rotary lime kiln',
            category: 'lime_kiln_parts',
            manufacturer: 'Metso',
            unitCost: 45000,
            currency: 'USD',
            leadTime: { value: 20, unit: 'weeks' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['recovery_boiler'],
            failureModes: ['corrosion', 'fatigue_cracking', 'thinning'],
            averageLifespan: { value: 15, unit: 'years' },
            specifications: { material: 'SA-516 Gr.70', thickness: '25mm' },
          },
        ],
      },
      {
        id: 'bleach_plant_parts',
        name: 'Bleach Plant Components',
        description: 'Mixer impellers, chemical dosing pumps, and components for bleach plant operation',
        parts: [
          {
            id: 'IMP-MIX-001',
            partNumber: 'IMP-MIX-001',
            name: 'Bleach Mixer Impeller',
            description: 'High-shear mixer impeller for ClO2 or peroxide mixing stage',
            category: 'bleach_plant_parts',
            manufacturer: 'Valmet',
            unitCost: 7500,
            currency: 'USD',
            leadTime: { value: 10, unit: 'weeks' as const },
            criticalityLevel: 'essential' as const,
            compatibleAssetTypes: ['refiner'],
            failureModes: ['erosion', 'corrosion', 'cavitation', 'fatigue_cracking'],
            averageLifespan: { value: 18, unit: 'months' },
            specifications: { material: 'Titanium Gr.2', diameter: '600mm', stages: 'Single-stage' },
          },
          {
            id: 'PUMP-DOSE-001',
            partNumber: 'PUMP-DOSE-001',
            name: 'Chemical Dosing Pump',
            description: 'Metering pump for precise ClO2 and NaOH chemical dosing',
            category: 'bleach_plant_parts',
            manufacturer: 'Prominent',
            unitCost: 3800,
            currency: 'USD',
            leadTime: { value: 4, unit: 'weeks' as const },
            criticalityLevel: 'essential' as const,
            compatibleAssetTypes: ['refiner'],
            failureModes: ['diaphragm_failure', 'check_valve_wear', 'stroke_drift'],
            averageLifespan: { value: 24, unit: 'months' },
            specifications: { type: 'Diaphragm metering', capacity: '500 L/h', material: 'PVDF wetted parts' },
          },
        ],
      },
      {
        id: 'refiner_parts',
        name: 'Refiner Components',
        description: 'Refiner plates, bearings, and accessories for disc refiners',
        parts: [
          {
            id: 'PLATE-REF-001',
            partNumber: 'PLATE-REF-001',
            name: 'Refiner Plate Set',
            description: 'Disc refiner plate set (rotor + stator) for LC refining',
            category: 'refiner_parts',
            manufacturer: 'Valmet',
            unitCost: 12000,
            currency: 'USD',
            leadTime: { value: 6, unit: 'weeks' as const },
            criticalityLevel: 'essential' as const,
            compatibleAssetTypes: ['refiner'],
            failureModes: ['wear', 'breakage', 'plugging'],
            averageLifespan: { value: 4, unit: 'months' },
            specifications: { material: 'Ni-Hard cast iron', pattern: 'Fine bar', segments: 'Full disc' },
          },
          {
            id: 'SEAL-REF-001',
            partNumber: 'SEAL-REF-001',
            name: 'Refiner Shaft Seal Assembly',
            description: 'Mechanical shaft seal for disc refiner preventing pulp leakage',
            category: 'refiner_parts',
            manufacturer: 'Flowserve',
            unitCost: 5500,
            currency: 'USD',
            leadTime: { value: 8, unit: 'weeks' as const },
            criticalityLevel: 'essential' as const,
            compatibleAssetTypes: ['refiner'],
            failureModes: ['seal_face_wear', 'spring_failure', 'thermal_damage'],
            averageLifespan: { value: 12, unit: 'months' },
            specifications: { type: 'Split mechanical seal', size: '6 inch', material: 'SiC faces, Viton elastomers' },
          },
        ],
      },
    ],
  },

  rootCauseLibrary: {
    categories: [
      // -----------------------------------------------------------------------
      // 1. Corrosion & Chemical Attack
      // -----------------------------------------------------------------------
      {
        id: 'corrosion_chemical',
        name: 'Corrosion & Chemical Attack',
        description: 'Root causes related to corrosion, chemical degradation, and material loss in kraft mill environments',
        icon: 'Droplets',
        color: '#f97316',
        rootCauses: [
          {
            id: 'black_liquor_corrosion',
            name: 'Black Liquor Corrosion',
            description: 'Accelerated corrosion of digester and evaporator internals from concentrated black liquor at elevated temperatures',
            category: 'corrosion_chemical',
            symptoms: [
              'Wall thickness loss detected by UT scanning',
              'Pinhole leaks in digester shell or piping',
              'Discoloration of external surfaces near welds',
              'Rising iron content in liquor samples',
              'Increased makeup chemical consumption',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Perform ultrasonic thickness survey on digester shell and nozzles',
                expectedResult: 'Identify areas below minimum wall thickness',
                tools: ['UT thickness gauge', 'Grid map'],
                duration: 120,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Analyze liquor chemistry for sulfidity, residual alkali, and chloride levels',
                expectedResult: 'Identify corrosive species concentration out of spec',
                tools: ['Lab analysis', 'Titration equipment'],
                duration: 60,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 3,
                action: 'Inspect weld seams and heat-affected zones with dye penetrant testing',
                expectedResult: 'Detect cracks or porosity in weld areas',
                tools: ['PT kit', 'UV light'],
                duration: 90,
                skillLevel: 'advanced' as const,
              },
            ],
            preventiveActions: [
              'Maintain proper liquor chemistry (sulfidity, residual alkali)',
              'Use duplex stainless steel for high-corrosion zones',
              'Schedule regular UT thickness surveys during outages',
              'Apply protective weld overlay on critical nozzles and transitions',
              'Monitor chloride ingress from bleach plant recirculation',
            ],
            typicalProbability: 0.18,
            affectedComponents: ['digester_shell', 'evaporator_tubes', 'piping', 'nozzles'],
            requiredExpertise: ['sme' as const, 'technician' as const],
            estimatedResolutionTime: { min: 8, typical: 24, max: 72, unit: 'hours' as const },
            costRange: { min: 10000, max: 250000, currency: 'USD' },
            industryReferences: ['TAPPI TIP 0402-16', 'BLRBAC Guidelines'],
          },
          {
            id: 'bleach_plant_corrosion',
            name: 'Bleach Plant Corrosion',
            description: 'Pitting and crevice corrosion in bleach plant equipment from ClO2, hypochlorite, and low-pH environments',
            category: 'corrosion_chemical',
            symptoms: [
              'Pitting on titanium or stainless steel surfaces',
              'Leaks at gasket flanges and expansion joints',
              'Degradation of FRP (fiberglass) linings',
              'Rising chloride content in wash water',
              'Discolored or weakened rubber linings',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Visual inspection of washer drums, towers, and piping during outage',
                expectedResult: 'Identify pitting, crevice corrosion, and lining deterioration',
                tools: ['Flashlight', 'Inspection mirror', 'Camera'],
                duration: 120,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Check pH and ORP at each bleaching stage',
                expectedResult: 'Verify chemical application is within design parameters',
                tools: ['Portable pH/ORP meter'],
                duration: 30,
                skillLevel: 'basic' as const,
              },
            ],
            preventiveActions: [
              'Use titanium or high-Mo stainless alloys in chlorine-contact zones',
              'Maintain proper pH control between bleaching stages',
              'Inspect and replace gaskets and expansion joints on schedule',
              'Monitor ORP to prevent over-application of oxidizing chemicals',
            ],
            typicalProbability: 0.12,
            affectedComponents: ['bleach_towers', 'washers', 'mixers', 'piping', 'linings'],
            requiredExpertise: ['sme' as const],
            estimatedResolutionTime: { min: 4, typical: 16, max: 48, unit: 'hours' as const },
            costRange: { min: 5000, max: 150000, currency: 'USD' },
          },
        ],
      },
      // -----------------------------------------------------------------------
      // 2. Mechanical Wear & Fatigue
      // -----------------------------------------------------------------------
      {
        id: 'mechanical_wear',
        name: 'Mechanical Wear & Fatigue',
        description: 'Root causes related to mechanical wear, fatigue cracking, and structural degradation of rotating and static equipment',
        icon: 'Cog',
        color: '#3b82f6',
        rootCauses: [
          {
            id: 'roll_cover_wear',
            name: 'Roll Cover Deterioration',
            description: 'Progressive degradation of paper machine roll covers (rubber, polyurethane, ceramic) affecting nip performance',
            category: 'mechanical_wear',
            symptoms: [
              'Uneven moisture profile across the sheet',
              'Sheet marking or embossing patterns',
              'Increased nip vibration',
              'Declining press dewatering efficiency',
              'Visible cover damage (grooving, pitting, delamination)',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Measure roll cover hardness with Shore durometer',
                expectedResult: 'Hardness within ±5 Shore A/D of specification',
                tools: ['Shore durometer', 'Hardness chart'],
                duration: 15,
                skillLevel: 'basic' as const,
              },
              {
                order: 2,
                action: 'Profile roll surface with profilometer or dial indicator',
                expectedResult: 'Surface roughness and crown within tolerance',
                tools: ['Profilometer', 'Dial indicator on traversing stand'],
                duration: 45,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 3,
                action: 'Perform nip impression test',
                expectedResult: 'Uniform nip width across the machine (±2mm)',
                tools: ['Carbon paper', 'Nip impression kit'],
                duration: 30,
                skillLevel: 'intermediate' as const,
              },
            ],
            preventiveActions: [
              'Schedule regular roll grinding based on condition monitoring',
              'Use proper doctor blade materials to avoid scoring',
              'Maintain correct nip pressures per manufacturer specs',
              'Keep felt and fabric clean to reduce abrasive wear',
              'Monitor nip vibration trending',
            ],
            typicalProbability: 0.20,
            affectedComponents: ['press_rolls', 'calender_rolls', 'suction_rolls', 'dryer_rolls'],
            requiredExpertise: ['technician' as const, 'sme' as const],
            estimatedResolutionTime: { min: 8, typical: 16, max: 48, unit: 'hours' as const },
            costRange: { min: 15000, max: 120000, currency: 'USD' },
          },
          {
            id: 'bearing_failure_pm',
            name: 'Paper Machine Bearing Failure',
            description: 'Bearing failures on paper machine rolls and pumps caused by contamination, misalignment, or lubrication issues in wet environments',
            category: 'mechanical_wear',
            symptoms: [
              'Elevated vibration at bearing frequencies (BPFO, BPFI)',
              'Rising bearing housing temperature',
              'Audible rumbling or grinding',
              'Water or stock contamination in grease samples',
              'Seal leakage around bearing housings',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Collect vibration spectrum on bearing housings',
                expectedResult: 'Identify bearing defect frequencies above baseline',
                tools: ['Vibration analyzer', 'Accelerometer'],
                duration: 20,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Sample bearing grease for moisture and particle contamination',
                expectedResult: 'Moisture <0.1%, no metallic particles',
                tools: ['Grease sampling kit', 'Lab analysis'],
                duration: 15,
                skillLevel: 'basic' as const,
              },
              {
                order: 3,
                action: 'Check bearing housing seals and lubrication lines',
                expectedResult: 'Seals intact, proper grease fill level',
                tools: ['Flashlight', 'Seal inspection'],
                duration: 10,
                skillLevel: 'basic' as const,
              },
            ],
            preventiveActions: [
              'Implement vibration-based condition monitoring on all critical rolls',
              'Use automatic grease lubrication systems',
              'Ensure proper labyrinth or lip seal designs for wet environments',
              'Follow manufacturer re-greasing intervals and quantities',
              'Track bearing life history for reliability improvement',
            ],
            typicalProbability: 0.22,
            affectedComponents: ['roll_bearings', 'pump_bearings', 'fan_bearings', 'dryer_journals'],
            requiredExpertise: ['technician' as const],
            estimatedResolutionTime: { min: 4, typical: 12, max: 24, unit: 'hours' as const },
            costRange: { min: 2000, max: 35000, currency: 'USD' },
            industryReferences: ['SKF Bearing Reliability in Paper Machines', 'ISO 10816-3'],
          },
        ],
      },
      // -----------------------------------------------------------------------
      // 3. Thermal Degradation
      // -----------------------------------------------------------------------
      {
        id: 'thermal_degradation',
        name: 'Thermal Degradation',
        description: 'Root causes related to thermal stress, overheating, and temperature cycling in boilers, dryers, and kilns',
        icon: 'Flame',
        color: '#dc2626',
        rootCauses: [
          {
            id: 'recovery_boiler_tube_failure',
            name: 'Recovery Boiler Tube Failure',
            description: 'Cracking or thinning of recovery boiler tubes from thermal cycling, smelt corrosion, and deposit-induced overheating',
            category: 'thermal_degradation',
            symptoms: [
              'Tube leaks detected by acoustic emission or visual steam',
              'Abnormal flue gas temperatures',
              'Increasing sootblower frequency required',
              'Smelt interaction burns or explosions risk indicators',
              'Wall thickness loss below BLRBAC minimums',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Perform UT thickness mapping of floor, wall, and superheater tubes during outage',
                expectedResult: 'Identify tubes below minimum wall thickness per BLRBAC',
                tools: ['UT thickness gauge', 'Tube mapping grid', 'Scaffold access'],
                duration: 480,
                skillLevel: 'advanced' as const,
              },
              {
                order: 2,
                action: 'Inspect tube surfaces for cracking using magnetic particle or dye penetrant',
                expectedResult: 'No cracks in composite tube welds or bend areas',
                tools: ['MT/PT equipment', 'Borescope'],
                duration: 240,
                skillLevel: 'advanced' as const,
              },
              {
                order: 3,
                action: 'Review thermal cycling history from DCS data',
                expectedResult: 'Identify excessive startup/shutdown cycles or temperature excursions',
                tools: ['DCS historian', 'Trend analysis software'],
                duration: 60,
                skillLevel: 'intermediate' as const,
              },
            ],
            preventiveActions: [
              'Follow BLRBAC inspection recommendations and minimum wall thickness standards',
              'Minimize thermal cycling (reduce emergency shutdowns)',
              'Optimize sootblower sequencing to prevent deposit buildup',
              'Use composite tubes (304L/SA210) in high-corrosion zones',
              'Monitor flue gas temperatures for localized hot spots',
            ],
            typicalProbability: 0.10,
            affectedComponents: ['floor_tubes', 'wall_tubes', 'superheater_tubes', 'economizer_tubes'],
            requiredExpertise: ['sme' as const],
            estimatedResolutionTime: { min: 24, typical: 72, max: 168, unit: 'hours' as const },
            costRange: { min: 50000, max: 500000, currency: 'USD' },
            industryReferences: ['BLRBAC Recommended Good Practice', 'TAPPI TIP 0402-19'],
          },
          {
            id: 'lime_kiln_refractory_failure',
            name: 'Lime Kiln Refractory Failure',
            description: 'Spalling, cracking, or ring formation in lime kiln refractory due to thermal shock, chemical attack, or mechanical stress',
            category: 'thermal_degradation',
            symptoms: [
              'Shell hot spots detected by IR scanning',
              'Increased shell temperature readings',
              'Ring formation restricting material flow',
              'Refractory chunks in product lime',
              'Kiln drive torque fluctuations',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Perform infrared thermographic survey of kiln shell',
                expectedResult: 'Identify hot spots indicating refractory loss',
                tools: ['IR camera', 'Shell temperature monitoring system'],
                duration: 60,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Inspect refractory lining during outage with visual and measurement',
                expectedResult: 'Measure remaining refractory thickness and identify spalling zones',
                tools: ['Measuring tape', 'Camera', 'Flashlight'],
                duration: 240,
                skillLevel: 'intermediate' as const,
              },
            ],
            preventiveActions: [
              'Monitor shell temperatures continuously with IR scanning system',
              'Avoid rapid temperature changes during startup and shutdown',
              'Maintain proper burner flame shape and position',
              'Control mud moisture and feed rate to prevent ring formation',
              'Schedule refractory relining based on condition rather than time',
            ],
            typicalProbability: 0.08,
            affectedComponents: ['refractory_lining', 'kiln_shell', 'nose_ring', 'dam_ring'],
            requiredExpertise: ['sme' as const, 'technician' as const],
            estimatedResolutionTime: { min: 48, typical: 120, max: 240, unit: 'hours' as const },
            costRange: { min: 100000, max: 800000, currency: 'USD' },
          },
        ],
      },
      // -----------------------------------------------------------------------
      // 4. Process Upsets
      // -----------------------------------------------------------------------
      {
        id: 'process_upsets',
        name: 'Process Upsets',
        description: 'Root causes related to process parameter deviations, grade changes, and operational disturbances',
        icon: 'AlertTriangle',
        color: '#ef4444',
        rootCauses: [
          {
            id: 'weak_sheet',
            name: 'Weak Sheet Strength',
            description: 'Insufficient wet or dry strength causing sheet breaks on the paper machine, often linked to stock preparation or chemical application issues',
            category: 'process_upsets',
            symptoms: [
              'Frequent sheet breaks at press or open draws',
              'Edge tears and web holes on the reel',
              'Low burst or tensile test results',
              'Excessive broke generation',
              'Difficulty threading during grade changes',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Check refining energy and freeness at stock prep',
                expectedResult: 'Freeness within target range for grade (e.g., 450-550 CSF)',
                tools: ['Freeness tester', 'Process data historian'],
                duration: 30,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Verify wet-strength and dry-strength chemical dosing rates',
                expectedResult: 'Chemical application rates within recipe specification',
                tools: ['Chemical metering data', 'Flow meters'],
                duration: 20,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 3,
                action: 'Sample and test handsheet strength from headbox stock',
                expectedResult: 'Burst and tensile within grade specification',
                tools: ['Handsheet former', 'Burst tester', 'Tensile tester'],
                duration: 45,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 4,
                action: 'Check retention and first-pass retention values',
                expectedResult: 'Retention >85% for total, >60% for fines',
                tools: ['Retention analyzer', 'White water sampling'],
                duration: 30,
                skillLevel: 'intermediate' as const,
              },
            ],
            preventiveActions: [
              'Optimize refining energy for target freeness per grade recipe',
              'Maintain consistent furnish blend ratios',
              'Ensure strength chemical dosing pump calibration',
              'Monitor first-pass retention and adjust retention aid',
              'Perform regular handsheet quality checks',
            ],
            typicalProbability: 0.15,
            affectedComponents: ['forming_section', 'press_section', 'open_draws', 'reel'],
            requiredExpertise: ['technician' as const, 'sme' as const],
            estimatedResolutionTime: { min: 1, typical: 4, max: 24, unit: 'hours' as const },
            costRange: { min: 5000, max: 50000, currency: 'USD' },
          },
          {
            id: 'digester_cook_upset',
            name: 'Digester Cook Upset',
            description: 'Off-target kappa number or yield due to incorrect cooking conditions, chip quality variation, or liquor imbalance',
            category: 'process_upsets',
            symptoms: [
              'Kappa number outside target range',
              'High or low pulp yield',
              'Excessive rejects from screening',
              'Uneven cook (hot and cold spots in batch digesters)',
              'Abnormal H-factor profiles',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Review cooking temperature and pressure profiles from DCS',
                expectedResult: 'Identify deviations from standard cook profile',
                tools: ['DCS historian', 'Cook profile charts'],
                duration: 30,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Test incoming chip quality (moisture, bark, species mix)',
                expectedResult: 'Chip quality within specification for grade being produced',
                tools: ['Chip classifier', 'Moisture analyzer'],
                duration: 45,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 3,
                action: 'Analyze white and black liquor composition',
                expectedResult: 'Effective alkali, sulfidity, and residual alkali within target',
                tools: ['Lab titration', 'Liquor analysis equipment'],
                duration: 60,
                skillLevel: 'intermediate' as const,
              },
            ],
            preventiveActions: [
              'Maintain consistent chip quality through wood yard controls',
              'Calibrate temperature and pressure instruments regularly',
              'Monitor H-factor in real-time with automated calculations',
              'Optimize liquor-to-wood ratio and chemical charge',
              'Track chip species mix and adjust cook parameters accordingly',
            ],
            typicalProbability: 0.12,
            affectedComponents: ['digester', 'blow_tank', 'screening', 'washing'],
            requiredExpertise: ['sme' as const],
            estimatedResolutionTime: { min: 2, typical: 8, max: 24, unit: 'hours' as const },
            costRange: { min: 10000, max: 100000, currency: 'USD' },
          },
        ],
      },
      // -----------------------------------------------------------------------
      // 5. Environmental Compliance Issues
      // -----------------------------------------------------------------------
      {
        id: 'environmental_compliance',
        name: 'Environmental Compliance Issues',
        description: 'Root causes of environmental exceedances including air emissions, effluent violations, and odor events',
        icon: 'Leaf',
        color: '#16a34a',
        rootCauses: [
          {
            id: 'trs_emission_exceedance',
            name: 'TRS Emission Exceedance',
            description: 'Total reduced sulfur emissions exceeding permit limits from recovery boiler, lime kiln, or DNCG/CNCG system failures',
            category: 'environmental_compliance',
            symptoms: [
              'TRS monitor readings above permit limits',
              'Odor complaints from surrounding community',
              'CEMS alarm activation',
              'Visible brown plume from recovery stack',
              'NCG system pressure or temperature anomalies',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Verify CEMS readings and calibration status',
                expectedResult: 'Confirm accurate TRS measurement (rule out instrument error)',
                tools: ['CEMS calibration data', 'Span gas'],
                duration: 30,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Check DNCG and CNCG collection and incineration systems',
                expectedResult: 'All NCG collection points operational, incinerator at temperature',
                tools: ['DCS data', 'Field inspection'],
                duration: 45,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 3,
                action: 'Review recovery boiler and lime kiln operating conditions',
                expectedResult: 'Combustion conditions optimal (temperature, O2, fuel feed)',
                tools: ['DCS historian', 'Stack analyzer'],
                duration: 30,
                skillLevel: 'intermediate' as const,
              },
            ],
            preventiveActions: [
              'Maintain NCG collection system integrity (leak detection program)',
              'Ensure backup NCG incineration capacity is available',
              'Optimize recovery boiler combustion for complete TRS destruction',
              'Calibrate CEMS per regulatory schedule',
              'Train operators on emergency NCG diversion procedures',
            ],
            typicalProbability: 0.08,
            affectedComponents: ['ncg_system', 'recovery_boiler', 'lime_kiln', 'evaporators'],
            requiredExpertise: ['sme' as const, 'analyst' as const],
            estimatedResolutionTime: { min: 2, typical: 8, max: 48, unit: 'hours' as const },
            costRange: { min: 5000, max: 200000, currency: 'USD' },
            industryReferences: ['EPA 40 CFR Part 63 Subpart S', 'TAPPI TIP 0400-60'],
          },
          {
            id: 'effluent_bod_exceedance',
            name: 'Effluent BOD/TSS Exceedance',
            description: 'Biological oxygen demand or total suspended solids in mill effluent exceeding NPDES permit limits',
            category: 'environmental_compliance',
            symptoms: [
              'Lab BOD/TSS results above permit limits',
              'Clarifier overflow turbidity increase',
              'Aeration basin dissolved oxygen dropping',
              'Sludge settleability declining (high SVI)',
              'Unusual foam or color in treatment system',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Sample and test influent and effluent BOD, TSS, and pH',
                expectedResult: 'Identify which parameter is out of compliance and treatment efficiency',
                tools: ['Sampling equipment', 'Lab analysis'],
                duration: 60,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Check aeration system and dissolved oxygen levels in treatment basins',
                expectedResult: 'DO >2.0 mg/L in aeration zones',
                tools: ['DO meter', 'Field observation'],
                duration: 30,
                skillLevel: 'basic' as const,
              },
            ],
            preventiveActions: [
              'Maintain wastewater treatment biological activity (nutrient dosing, pH control)',
              'Monitor influent loading to prevent hydraulic or organic shock',
              'Ensure primary clarifier and spill collection systems function properly',
              'Control in-mill losses (fiber, chemical, broke pit overflows)',
              'Maintain aeration equipment and blower capacity',
            ],
            typicalProbability: 0.06,
            affectedComponents: ['wastewater_treatment', 'clarifiers', 'aeration_basins', 'outfall'],
            requiredExpertise: ['analyst' as const, 'sme' as const],
            estimatedResolutionTime: { min: 4, typical: 24, max: 168, unit: 'hours' as const },
            costRange: { min: 10000, max: 500000, currency: 'USD' },
            industryReferences: ['EPA Cluster Rules 40 CFR 430', 'NPDES Permit Requirements'],
          },
        ],
      },
      // -----------------------------------------------------------------------
      // 6. Instrumentation Failures
      // -----------------------------------------------------------------------
      {
        id: 'instrumentation_failures',
        name: 'Instrumentation Failures',
        description: 'Root causes related to sensor drift, control loop failures, and DCS/PLC malfunctions in mill process control',
        icon: 'Gauge',
        color: '#8b5cf6',
        rootCauses: [
          {
            id: 'consistency_transmitter_drift',
            name: 'Consistency Transmitter Drift',
            description: 'Microwave or blade-type consistency transmitters losing accuracy due to coating buildup, wear, or calibration drift',
            category: 'instrumentation_failures',
            symptoms: [
              'Basis weight variation despite stable headbox controls',
              'Inconsistent sheet properties between lab and on-machine measurements',
              'Control valve hunting on dilution lines',
              'Operator complaints about "unreliable" consistency readings',
              'Grade change transitions taking longer than expected',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Grab sample and compare lab consistency to transmitter reading',
                expectedResult: 'Difference <0.1% absolute for blade, <0.2% for microwave',
                tools: ['Sample bucket', 'Lab consistency tester', 'DCS trend'],
                duration: 20,
                skillLevel: 'basic' as const,
              },
              {
                order: 2,
                action: 'Inspect sensor element for coating buildup or mechanical damage',
                expectedResult: 'Clean sensor surface, no blade wear beyond tolerance',
                tools: ['Flashlight', 'Scraper', 'Micrometer'],
                duration: 30,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 3,
                action: 'Check 4-20mA signal output versus actual reading',
                expectedResult: 'Linear signal within ±0.1mA of expected value',
                tools: ['mA clamp meter', 'Calibration data'],
                duration: 15,
                skillLevel: 'intermediate' as const,
              },
            ],
            preventiveActions: [
              'Schedule regular sensor cleaning during grade changes or shutdowns',
              'Perform monthly grab-sample calibration verification',
              'Replace blade-type sensor elements on wear schedule',
              'Maintain proper installation (flow velocity, orientation)',
              'Use redundant consistency measurement for critical control loops',
            ],
            typicalProbability: 0.14,
            affectedComponents: ['consistency_transmitters', 'stock_prep_instruments', 'headbox_controls'],
            requiredExpertise: ['technician' as const],
            estimatedResolutionTime: { min: 1, typical: 4, max: 8, unit: 'hours' as const },
            costRange: { min: 500, max: 8000, currency: 'USD' },
          },
          {
            id: 'qcs_scanner_failure',
            name: 'QCS Scanner Malfunction',
            description: 'Quality Control System scanner frame or sensor head failures affecting on-line basis weight, moisture, and caliper measurement',
            category: 'instrumentation_failures',
            symptoms: [
              'QCS scanner stops traversing or shows intermittent data',
              'Loss of profile data for basis weight, moisture, or caliper',
              'CD control valves go to manual fallback positions',
              'Increasing off-spec production due to loss of closed-loop control',
              'Scanner beam ratio or standardization errors',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Check scanner drive system (motor, belt, encoder, rails)',
                expectedResult: 'Scanner traverses smoothly without hesitation or binding',
                tools: ['Visual inspection', 'Spare belt'],
                duration: 30,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Run sensor standardization sequence',
                expectedResult: 'All sensors pass standardization within tolerance',
                tools: ['QCS software', 'Standardization samples'],
                duration: 20,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 3,
                action: 'Inspect sensor head windows for contamination or damage',
                expectedResult: 'Clean, undamaged sensor windows with proper air purge',
                tools: ['Cleaning supplies', 'Compressed air'],
                duration: 15,
                skillLevel: 'basic' as const,
              },
            ],
            preventiveActions: [
              'Clean sensor windows during every scheduled shutdown',
              'Maintain scanner air purge system (filters, regulators, flow)',
              'Replace scanner drive belts and encoder on schedule',
              'Perform monthly standardization checks',
              'Keep spare sensor heads available for critical measurements',
            ],
            typicalProbability: 0.10,
            affectedComponents: ['qcs_scanner', 'sensor_heads', 'scanner_frame', 'cd_controls'],
            requiredExpertise: ['technician' as const, 'sme' as const],
            estimatedResolutionTime: { min: 2, typical: 6, max: 16, unit: 'hours' as const },
            costRange: { min: 2000, max: 50000, currency: 'USD' },
            industryReferences: ['Honeywell/ABB/Valmet QCS Service Manuals'],
          },
        ],
      },
    ],
    commonPatterns: [
      {
        id: 'felt_contamination',
        name: 'Felt Contamination Pattern',
        description: 'Progressive felt filling reducing dewatering, leading to wet sheet and breaks',
        sequence: ['Stock contamination or pitch deposit', 'Felt pore filling and compaction', 'Reduced felt permeability', 'Poor press dewatering', 'High moisture entering dryers', 'Increased energy consumption or wet web breaks'],
        indicators: ['Increasing moisture after press section', 'Reduced felt permeability (vacuum measurement)', 'Rising dryer steam consumption'],
        preventionStrategy: 'Regular felt conditioning (high-pressure showers, chemical cleaning), maintain stock cleaner efficiency, and control pitch/stickies in furnish',
      },
      {
        id: 'recovery_boiler_plugging',
        name: 'Recovery Boiler Plugging Cascade',
        description: 'Progressive deposit buildup leading to reduced heat transfer and potential forced shutdown',
        sequence: ['Carryover increase from black liquor firing', 'Deposit formation on superheater tubes', 'Reduced heat transfer and steam temperature drop', 'Increased sootblowing frequency', 'Pluggage bridges between tube banks', 'Forced shutdown for manual cleaning'],
        indicators: ['Flue gas temperature differential increasing', 'Superheater steam temperature trending down', 'Sootblower effectiveness declining'],
        preventionStrategy: 'Optimize black liquor firing parameters, maintain proper combustion air distribution, and implement intelligent sootblower sequencing based on flue gas temperature monitoring',
      },
      {
        id: 'digester_scaling_cycle',
        name: 'Digester Scaling Cycle',
        description: 'Calcium carbonate or calcium oxalate scale buildup in digester extraction screens and piping',
        sequence: ['Elevated calcium in wood chips or white liquor', 'Scale nucleation on screen surfaces', 'Progressive screen plugging', 'Reduced circulation flow', 'Uneven cook and kappa variation', 'Forced shutdown for acid cleaning'],
        indicators: ['Extraction screen differential pressure rising', 'Circulation flow declining', 'Kappa variability increasing'],
        preventionStrategy: 'Monitor calcium levels in white liquor, optimize dead load, and schedule preventive acid washing of screens based on differential pressure trends',
      },
    ],
  },

  laborRates: {
    roles: [
      { roleId: 'machine_tender', name: 'Machine Tender', hourlyRate: 45, currency: 'USD', certifications: ['Paper Machine Operation'] },
      { roleId: 'millwright', name: 'Millwright', hourlyRate: 50, currency: 'USD', certifications: ['Millwright'] },
      { roleId: 'process_engineer', name: 'Process Engineer', hourlyRate: 85, currency: 'USD', certifications: ['TAPPI Certified'] },
    ],
    overtimeMultiplier: 1.5,
    emergencyMultiplier: 2.0,
    weekendMultiplier: 1.5,
  },
}
